import React, { Component } from 'react';
import { connect } from 'react-redux';
import { format, getYear } from 'date-fns';
import _, { filter, includes } from 'lodash';
import {
  getCalendarRoute, hasCalendarRequestExpired, isUserAdmin, isUserOwner,
} from '../../helpers';
import {
  changeNavWeek, getDatesForWeek, getNavDates, getNavToday, getSelectedWeekDates, getWeekBeginningMon,
} from '../../helpers/dates';
import { withRouter } from '../../helpers/withRouter';
import {
  addDraggedLog,
  addLog,
  changeDates,
  changeSelectedUser,
  closeModal,
  openModal,
  removeDraggedLog,
  removeLog,
  requestLogs,
  updateLog,
  updateModal,
} from '../../redux/actions';
import { fetchDeskLabels } from '../../redux/actions/desk-bookings';
import { fetchWorkSessions } from '../../redux/actions/work-sessions';
import { LOG_ADD_MODAL, LOG_COPY_MODAL, LOG_EDIT_MODAL, MARKETING_MODAL, PAYROLL_MODAL } from '../../redux/reducers/modal';
import CalendarContent from '../blocks/calendar/CalendarContent';
import CalendarTimeLogModal from '../blocks/calendar/CalendarTimeLogModal';
import CopyTimeLogModal from '../blocks/calendar/CopyTimeLogModal';
import MiniWeeklyStatistics from '../blocks/calendar/MiniWeeklyStatistics';
import MarketingStatsModal from '../blocks/marketing/MarketingStatsModal';
import ReduxModal from '../blocks/modal/ReduxModal';
import NavContent from '../blocks/navigation/NavContent';
import PayrollGeneratorModal from '../blocks/payroll/PayrollGeneratorModal';
import type {
  DeskLabelsState, Holiday, Log, LogRequest, Logs, Project, State as ReduxState, RequestLogs, Router, SingleModalState, User, WorkSessionsState,
} from '../../types';

type ComponentState = {
  modal: {
    date: any,
    edit: boolean,
    title: string,
    saveBtnText: string,
    log?: Log | null,
  },
  copyModal: {
    display: boolean,
    log: any,
  },
  miniStatsDisplayed: boolean,
}

type Props = {
  logs: Logs,
  currentUser: User,
  users: Array<User>,
  daysInWeek: Array<any>,
  projects: Array<Project>,
  categories: Array<any>,
  dates: Array<string>,
  selectedUser: User,
  requests: any,
  routeParams: any,
  onRemoveLog: any,
  onAddLog: any,
  onUpdateLog: any,
  onLogsChange: any,
  onSelectedUserChange: any,
  onChangeDates: any,
  onAddDraggedLog: any,
  onRemoveDraggedLog: any,
  onOpenModal: any,
  onCloseModal: any,
  onUpdateModal: any,
  onFetchWorkSessions: (startDate: string, endDate: string, userId?: number) => void,
  onFetchDeskLabels: (startDate: string, endDate: string, userId?: number) => void,
  draggedLog: Log,
  location: any,
  projectsWeeklyHours: any,
  holidays: Array<Holiday>,
  vacations: any,
  router: Router,
  workSessions: WorkSessionsState,
  deskLabels: DeskLabelsState,
}

class Timesheets extends Component<Props, ComponentState> {
  state = {
    modal: {
      date: null,
      edit: false,
      saveBtnText: '',
      title: '',
      log: null,
    },
    copyModal: {
      display: false,
      log: {},
    },
    miniStatsDisplayed: false,
  };

  componentDidMount = () => {
    const {
      routeParams,
      selectedUser,
      dates,
      onLogsChange,
      currentUser,
    } = this.props;

    const year = routeParams?.year || getYear(new Date(dates[0]));
    const weekNum = routeParams?.weekNum || getWeekBeginningMon(new Date(dates[0]));
    const selectedUserId = selectedUser?.id || currentUser.id;

    // Fetch work sessions for the current week
    const weekDates = getSelectedWeekDates(year, weekNum);
    const startDate = weekDates[0];
    const endDate = weekDates[weekDates.length - 1];
    const userId = (isUserAdmin(currentUser) || isUserOwner(currentUser)) ? selectedUserId : undefined;
    this.fetchWeekDetails(startDate, endDate, userId);

    if (routeParams?.year && routeParams?.weekNum) {
      onLogsChange({
        weekNum,
        year,
        selectedUserId,
      });
    }
  };

  componentDidUpdate = (prevProps: Props) => {
    const {
      routeParams: { year, weekNum },
      selectedUser: { id: selectedUserId },
      router: { location },
      onLogsChange,
      onChangeDates,
      requests,
      currentUser,
    } = this.props;
    const searchChanged = prevProps.router.location.search !== location.search;
    const weekChanged = prevProps.routeParams.weekNum !== weekNum;
    const yearChanged = prevProps.routeParams.year !== year;
    const userChanged = prevProps.selectedUser.id !== selectedUserId;

    if (searchChanged || weekChanged || yearChanged || userChanged) {
      if (!location.search && (!year || !weekNum)) {
        this.handleNavTodayClick();
      } else if (hasCalendarRequestExpired(year, weekNum, selectedUserId, requests) || searchChanged) {
        if (weekNum && year) {
          onLogsChange({
            weekNum,
            year,
            selectedUserId,
          });

          const weekDates = getSelectedWeekDates(year, weekNum);
          const startDate = weekDates[0];
          const endDate = weekDates[weekDates.length - 1];
          const userId = (isUserAdmin(currentUser) || isUserOwner(currentUser)) ? selectedUserId : undefined;
          this.fetchWeekDetails(startDate, endDate, userId);
        }
      } else {
        onChangeDates(getSelectedWeekDates(year, weekNum));
        if (weekNum && year) {
          const weekDates = getSelectedWeekDates(year, weekNum);
          const startDate = weekDates[0];
          const endDate = weekDates[weekDates.length - 1];
          const userId = (isUserAdmin(currentUser) || isUserOwner(currentUser)) ? selectedUserId : undefined;
          this.fetchWeekDetails(startDate, endDate, userId);
        }
      }
    }
  };

  // Check-ins and desk bookings shown in the day headings.
  fetchWeekDetails = (startDate: string, endDate: string, userId?: number) => {
    const { onFetchWorkSessions, onFetchDeskLabels } = this.props;
    onFetchWorkSessions(startDate, endDate, userId);
    onFetchDeskLabels(startDate, endDate, userId);
  };

  getLogModalId = () => {
    const { modal: { edit } } = this.state;

    return edit ? LOG_EDIT_MODAL : LOG_ADD_MODAL;
  };

  handleOpenAddModal = (date: string) => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: LOG_ADD_MODAL });
    this.setState({
      modal: {
        date,
        title: 'Add a log',
        saveBtnText: 'Save new log',
        edit: false,
        log: null,
      },
    });
  };

  handleOpenEditModal = (log: Log, date: string) => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: LOG_EDIT_MODAL });
    this.setState({
      modal: {
        date,
        title: 'Edit log',
        saveBtnText: 'Save',
        edit: true,
        log,
      },
    });
  };

  handleAddModalClose = () => this.handleModalClose(LOG_ADD_MODAL);

  handleEditModalClose = () => this.handleModalClose(LOG_EDIT_MODAL);

  handleModalClose = (id: string) => {
    const { onCloseModal } = this.props;
    onCloseModal({ id });
    this.setState({
      modal: {
        date: null,
        edit: false,
        saveBtnText: '',
        title: '',
        log: null,
      },
    });
  };

  handleCopyModalClose = () => {
    const { onRemoveDraggedLog, onCloseModal } = this.props;
    onCloseModal({ id: LOG_COPY_MODAL });
    this.setState({ copyModal: { display: false, log: {} } });
    onRemoveDraggedLog();
  };

  handleSubmitLogForm = (formData: any) => {
    const { modal: { date, edit } } = this.state;
    const { onAddLog, onUpdateLog, selectedUser } = this.props;
    this.disableFormControls(edit ? LOG_EDIT_MODAL : LOG_ADD_MODAL);
    formData.date = date;
    formData.user_id = selectedUser.id;
    formData.user = selectedUser;
    if (!edit) {
      onAddLog(formData.project_id, formData).then(this.handleAddModalClose);
    } else {
      onUpdateLog(formData.project_id, formData.id, formData)
        .then(this.handleEditModalClose);
    }
  };

  handleDeleteLog = (log: Log) => {
    const { onRemoveLog } = this.props;
    this.disableFormControls(LOG_EDIT_MODAL);
    onRemoveLog(log.project_id, log.id).then(this.handleEditModalClose);
  };

  disableFormControls = (id: string) => {
    const { onUpdateModal } = this.props;
    onUpdateModal({ id, disableButtons: true });
  };

  handleNavTodayClick = () => {
    const { router: { navigate }, selectedUser } = this.props;
    const { year, week } = getNavToday();
    navigate(getCalendarRoute(year, week, selectedUser?.id || null));
  };

  handleNavYearCalendarClick = () => {
    const { router } = this.props;
    router.navigate('/year-calendar');
  };

  handlePayrollGeneratorClick = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: PAYROLL_MODAL });
  };

  handlePayrollModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: PAYROLL_MODAL });
  };

  handleMarketingStatsClick = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: MARKETING_MODAL });
  };

  handleMarketingModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: MARKETING_MODAL });
  };

  handleNavPrevWeekClick = () => this.changeNavWeek(true);

  handleNavNextWeekClick = () => this.changeNavWeek(false);

  changeNavWeek = (subWeek: boolean) => {
    const { dates, selectedUser } = this.props;
    const { navigate } = this.props.router;
    const { year, week } = changeNavWeek(dates, subWeek);
    navigate(getCalendarRoute(year, week, selectedUser?.id || null));
  };

  handleSelectedUserChange = (user: User) => {
    const {
      routeParams, dates, router, onSelectedUserChange,
    } = this.props;
    const year = routeParams.year || getYear(new Date(dates[0]));
    const weekNum = routeParams.weekNum || getWeekBeginningMon(new Date(dates[0]));
    if (user) {
      onSelectedUserChange(user).then(() => {
        router.navigate(getCalendarRoute(year, weekNum, user.id));
      });
    }
  };

  handleCopyLogByDrop = (projectId: number, data: any) => {
    const { onAddLog, onOpenModal } = this.props;
    const { copyModal } = this.state;
    const day = format(new Date(data.date), 'EEEE');
    if (['Saturday', 'Sunday'].includes(day)) {
      onOpenModal({ id: LOG_COPY_MODAL });
      this.setState({ copyModal: { ...copyModal, display: true, log: data } });
    } else {
      onAddLog(projectId, data);
    }
  };

  handleToggleStatistics = () => {
    const { miniStatsDisplayed } = this.state;
    if (miniStatsDisplayed) {
      this.setState({ miniStatsDisplayed: false });
    } else {
      this.setState({ miniStatsDisplayed: true });
    }
  };

  handleCloseStatistics = () => {
    this.setState({ miniStatsDisplayed: false });
  };

  getLogIdsForWeekday = (date: string) => {
    const { logs: { logsByWeekday }, selectedUser } = this.props;
    const selectedUserId = selectedUser.id;
    if (logsByWeekday[date]) {
      // const logs = _.compact(logsByWeekday[date]);
      return _.flatMap(logsByWeekday[date].filter(log => _.toInteger(log.userId) === _.toInteger(selectedUserId)), log => log.id);
    }

    return [];
  };

  getLogsForWeek = () => {
    const {
      currentUser, dates, logs, routeParams,
    } = this.props;
    const isAdmin = isUserAdmin(currentUser);
    const isOwner = isUserOwner(currentUser);

    if (isOwner || isAdmin) return [];

    const year = routeParams.year || getYear(new Date(dates[0]));
    const weekNum = routeParams.weekNum || getWeekBeginningMon(new Date(dates[0]));
    const selectedWeekDates = getSelectedWeekDates(year, weekNum);

    return filter(logs.items, (log) => includes(selectedWeekDates, log.date));
  };

  render(): React.ReactNode {
    const {
      currentUser, selectedUser, users, daysInWeek, projects, categories, logs,
      onAddDraggedLog, draggedLog, onAddLog, projectsWeeklyHours, dates: datesRaw,
      holidays, vacations, workSessions, deskLabels,
    } = this.props;
    const { modal, copyModal, miniStatsDisplayed } = this.state;
    const projectsHours = projectsWeeklyHours[`${getWeekBeginningMon(new Date(datesRaw[0]))}`];
    const dates = getDatesForWeek(datesRaw);
    const navDates = getNavDates(dates.map(({ date }) => new Date(date)));
    const logsForWeek = this.getLogsForWeek();
    const selectedUserVacationDates = _.values(vacations).filter(({ id }) => id === selectedUser.id)[0].usedVacationDates.map(({ date }) => date);

    return (
      <div className="timesheets-view view-wrapper">
        <NavContent
          navDates={navDates}
          currentUser={currentUser}
          selectedUser={selectedUser}
          users={users}
          onNavTodayClick={this.handleNavTodayClick}
          onNavPrevWeekClick={this.handleNavPrevWeekClick}
          onNavNextWeekClick={this.handleNavNextWeekClick}
          onSelectedUserChange={this.handleSelectedUserChange}
          onToggleStatistics={this.handleToggleStatistics}
          onNavYearCalendarClick={this.handleNavYearCalendarClick}
          onPayrollGeneratorClick={this.handlePayrollGeneratorClick}
          onMarketingStatsClick={this.handleMarketingStatsClick}

          isCalendar
        />
        {miniStatsDisplayed && (
          <MiniWeeklyStatistics
            onCloseClick={this.handleCloseStatistics}
            projects={projects}
            projectsHours={projectsHours}
            logs={logsForWeek}
            currentUser={currentUser}
          />
        )}
        <CalendarContent
          onOpenEditModal={this.handleOpenEditModal}
          onOpenAddModal={this.handleOpenAddModal}
          getLogIdsForWeekday={this.getLogIdsForWeekday}
          dates={dates}
          selectedUserId={selectedUser.id}
          logsById={logs.logsById}
          daysInWeek={daysInWeek}
          projects={projects}
          onAddLog={(projectId: any, formData: any) => Promise.resolve(this.handleCopyLogByDrop(projectId, formData))}
          onAddDraggedLog={onAddDraggedLog}
          draggedLog={draggedLog}
          weekendDrop={copyModal.display}
          holidays={holidays}
          selectedUserVacationDates={selectedUserVacationDates}
          currentUser={currentUser}
          workSessions={workSessions}
          deskLabelsByDate={deskLabels.byDate}
        />
        <ReduxModal id={this.getLogModalId()}>
          <CalendarTimeLogModal
            {...modal}
            projects={projects}
            categories={categories}
            onSubmitLogForm={this.handleSubmitLogForm}
            onDeleteLog={this.handleDeleteLog}
            onCloseModal={modal.edit ? this.handleEditModalClose : this.handleAddModalClose}
            currentUser={currentUser}
          />
        </ReduxModal>
        <ReduxModal id={LOG_COPY_MODAL}>
          <CopyTimeLogModal
            log={copyModal.log}
            onCloseModal={this.handleCopyModalClose}
            onSubmit={onAddLog}
          />
        </ReduxModal>
        <ReduxModal id={PAYROLL_MODAL}>
          <PayrollGeneratorModal
            onCloseModal={this.handlePayrollModalClose}
          />
        </ReduxModal>
        <ReduxModal id={MARKETING_MODAL}>
          <MarketingStatsModal
            onCloseModal={this.handleMarketingModalClose}
          />
        </ReduxModal>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: any) => ({
  logs: state.logs,
  currentUser: state.currentUser,
  users: state.users,
  daysInWeek: state.daysInWeek,
  projects: state.projects?.items || [],
  dates: state.dates,
  selectedUser: state.selectedUser,
  requests: state.requests.calendar,
  routeParams: ownProps.router?.params || {},
  location: ownProps.router?.location || {},
  categories: state.categories,
  draggedLog: state.logs.draggedLog,
  projectsWeeklyHours: state.projectsWeeklyHours,
  holidays: state.holidays,
  vacations: state.vacations,
  workSessions: state.workSessions,
  deskLabels: state.deskLabels,
});

const mapDispatchToProps = dispatch => ({
  onRemoveLog: (projectId: number, logId: number) => dispatch(removeLog(projectId, logId)),
  onAddLog: (projectId: number, log: LogRequest) => dispatch(addLog(projectId, log)),
  onUpdateLog: (projectId: number, logId: number, log: LogRequest) => dispatch(updateLog(projectId, logId, log)),
  onLogsChange: (logInfo: RequestLogs) => dispatch(requestLogs(logInfo)),
  onSelectedUserChange: (selectedUserId: number) => dispatch(changeSelectedUser(selectedUserId)),
  onChangeDates: (dateInfo: Array<string>) => dispatch(changeDates(dateInfo)),
  onAddDraggedLog: (log: Log) => dispatch(addDraggedLog(log)),
  onRemoveDraggedLog: () => dispatch(removeDraggedLog(undefined as any)),
  onOpenModal: (modal: SingleModalState) => dispatch(openModal(modal)),
  onCloseModal: (modal: SingleModalState) => dispatch(closeModal(modal)),
  onUpdateModal: (modal: SingleModalState) => dispatch(updateModal(modal)),
  onFetchWorkSessions: (startDate: string, endDate: string, userId?: number) => dispatch(fetchWorkSessions(startDate, endDate, userId)),
  onFetchDeskLabels: (startDate: string, endDate: string, userId?: number) => dispatch(fetchDeskLabels(startDate, endDate, userId)),
});

const ConnectedTimesheets = withRouter(connect(mapStateToProps, mapDispatchToProps)(Timesheets as any));

export default ConnectedTimesheets;
